import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Brain,
  Plus,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  Send,
  Eye,
  Download,
  Filter,
  Search,
  RefreshCw,
  BarChart3,
  BookOpen,
  FileCheck,
  AlertTriangle,
  X,
  ExternalLink,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PlanStudioDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    planType: '',
    year: '',
    search: ''
  });
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', plan: null });
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');

  useEffect(() => {
    if (!isYomevUser) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    fetchData();
  }, [isYomevUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [plansRes, statsRes] = await Promise.all([
        fetch(`${API}/api/plan-studio/plans?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/api/plan-studio/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Taslak', icon: FileText },
      in_review: { color: 'bg-yellow-100 text-yellow-700', label: 'İncelemede', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', label: 'Onaylandı', icon: CheckCircle },
      published: { color: 'bg-blue-100 text-blue-700', label: 'Yayında', icon: Send },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Reddedildi', icon: FileText }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const getPlanTypeBadge = (type) => {
    const typeConfig = {
      CORE: { color: 'bg-purple-100 text-purple-700', label: 'Temel' },
      TURKCE: { color: 'bg-blue-100 text-blue-700', label: 'Türkçe' },
      MATEMATIK: { color: 'bg-orange-100 text-orange-700', label: 'Matematik' }
    };
    const config = typeConfig[type] || { color: 'bg-gray-100 text-gray-700', label: type };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const filteredPlans = plans.filter(plan => {
    if (filters.status && plan.status !== filters.status) return false;
    if (filters.planType && plan.planType !== filters.planType) return false;
    if (filters.year && plan.year !== parseInt(filters.year)) return false;
    return true;
  });

  // PDF Preview and Download functions
  const handlePreviewPdf = async (plan) => {
    if (plan.pdfUrl) {
      setPdfPreview({ open: true, url: plan.pdfUrl, plan });
    } else {
      // Generate PDF on the fly if not available
      try {
        const res = await fetch(`${API}/api/plan-studio/plans/${plan.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.pdfUrl) {
            setPdfPreview({ open: true, url: data.pdfUrl, plan });
          }
        }
      } catch (error) {
        console.error('PDF fetch error:', error);
      }
    }
  };

  const handleDownloadPdf = async (plan) => {
    setDownloadingPdf(plan.id);
    try {
      let pdfUrl = plan.pdfUrl;
      
      // If no PDF URL, fetch it from the API
      if (!pdfUrl) {
        const res = await fetch(`${API}/api/plan-studio/plans/${plan.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          pdfUrl = data.pdfUrl;
        }
      }

      if (pdfUrl) {
        // Open PDF in new tab for download
        window.open(pdfUrl, '_blank');
      }
    } catch (error) {
      console.error('PDF download error:', error);
    } finally {
      setDownloadingPdf(null);
    }
  };

  const closePdfPreview = () => {
    setPdfPreview({ open: false, url: '', plan: null });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Erişim Engellendi</h2>
        <p className="text-gray-600 max-w-md">
          Plan Studio modülü sadece <strong>@yomev.org</strong> uzantılı email adresine sahip kullanıcılar tarafından kullanılabilir.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Dashboard'a Dön
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plan-studio-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-8 h-8 text-blue-600" />
            Plan Studio (AI)
          </h1>
          <p className="text-gray-600 mt-1">AI destekli haftalık plan oluşturucu</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/plan-studio/analytics"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="analytics-btn"
          >
            <BarChart3 size={18} />
            Analitik
          </Link>
          <Link
            to="/plan-studio/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="new-plan-btn"
          >
            <Plus size={20} />
            Yeni Plan Oluştur
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalPlans}</p>
                <p className="text-xs text-gray-500">Toplam Plan</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inReviewPlans}</p>
                <p className="text-xs text-gray-500">İncelemede</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.approvedPlans}</p>
                <p className="text-xs text-gray-500">Onaylı</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.publishedPlans}</p>
                <p className="text-xs text-gray-500">Yayında</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpen className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalActivities}</p>
                <p className="text-xs text-gray-500">Etkinlik</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.totalWeekMaps}</p>
                <p className="text-xs text-gray-500">Hafta Haritası</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtrele:</span>
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="filter-status"
          >
            <option value="">Tüm Durumlar</option>
            <option value="draft">Taslak</option>
            <option value="in_review">İncelemede</option>
            <option value="approved">Onaylandı</option>
            <option value="published">Yayında</option>
          </select>
          <select
            value={filters.planType}
            onChange={(e) => setFilters({...filters, planType: e.target.value})}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="filter-type"
          >
            <option value="">Tüm Türler</option>
            <option value="CORE">Temel</option>
            <option value="TURKCE">Türkçe</option>
            <option value="MATEMATIK">Matematik</option>
          </select>
          <select
            value={filters.year}
            onChange={(e) => setFilters({...filters, year: e.target.value})}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            data-testid="filter-year"
          >
            <option value="">Tüm Yıllar</option>
            <option value="1">1. Yıl</option>
            <option value="2">2. Yıl</option>
            <option value="3">3. Yıl</option>
          </select>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Yenile
          </button>
        </div>
      </div>

      {/* Plans Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tür</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Yıl/Hafta</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oluşturulma</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPlans.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Henüz plan oluşturulmamış</p>
                    <Link
                      to="/plan-studio/new"
                      className="mt-3 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                    >
                      <Plus size={16} />
                      İlk planı oluştur
                    </Link>
                  </td>
                </tr>
              ) : (
                filteredPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50" data-testid={`plan-row-${plan.id}`}>
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900">
                          {plan.contentJson?.theme || `${plan.year}. Yıl - ${plan.week}. Hafta Planı`}
                        </p>
                        <p className="text-sm text-gray-500">
                          {plan.contentJson?.mainCompetency?.substring(0, 60) || 'İçerik bekleniyor...'}
                          {plan.contentJson?.mainCompetency?.length > 60 ? '...' : ''}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getPlanTypeBadge(plan.planType)}
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900">{plan.year}. Yıl</span>
                      <span className="text-gray-400 mx-1">/</span>
                      <span className="text-sm text-gray-600">{plan.week}. Hafta</span>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(plan.status)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(plan.createdAt).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/plan-studio/review/${plan.id}`}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="İncele"
                          data-testid={`view-plan-${plan.id}`}
                        >
                          <Eye size={18} />
                        </Link>
                        {(plan.pdfUrl || plan.contentJson) && (
                          <>
                            <button
                              onClick={() => handlePreviewPdf(plan)}
                              className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="PDF Önizle"
                              data-testid={`preview-pdf-${plan.id}`}
                            >
                              <FileText size={18} />
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(plan)}
                              disabled={downloadingPdf === plan.id}
                              className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title="PDF İndir"
                              data-testid={`download-pdf-${plan.id}`}
                            >
                              {downloadingPdf === plan.id ? (
                                <Loader2 size={18} className="animate-spin" />
                              ) : (
                                <Download size={18} />
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/plan-studio/admin/week-maps"
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
              <Calendar className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Hafta Haritaları</h3>
              <p className="text-sm text-gray-500">Yıllık program haritasını yönet</p>
            </div>
          </div>
        </Link>
        <Link
          to="/plan-studio/admin/activities"
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Etkinlik Kütüphanesi</h3>
              <p className="text-sm text-gray-500">Etkinlikleri düzenle ve ekle</p>
            </div>
          </div>
        </Link>
        <Link
          to="/plan-studio/admin/glossary"
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg group-hover:bg-amber-200 transition-colors">
              <FileCheck className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Terim Sözlüğü</h3>
              <p className="text-sm text-gray-500">Türkçe terim standardı</p>
            </div>
          </div>
        </Link>
      </div>

      {/* PDF Preview Modal */}
      {pdfPreview.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto" data-testid="pdf-preview-modal">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closePdfPreview}
            />
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {pdfPreview.plan?.contentJson?.theme || 'Plan PDF Önizleme'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {pdfPreview.plan?.year}. Yıl - {pdfPreview.plan?.week}. Hafta • 
                      {pdfPreview.plan?.planType === 'CORE' ? ' Temel' : 
                       pdfPreview.plan?.planType === 'TURKCE' ? ' Türkçe' : 
                       pdfPreview.plan?.planType === 'MATEMATIK' ? ' Matematik' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={pdfPreview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    data-testid="pdf-open-new-tab"
                  >
                    <ExternalLink size={16} />
                    Yeni Sekmede Aç
                  </a>
                  <button
                    onClick={() => window.open(pdfPreview.url, '_blank')}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    data-testid="pdf-download-btn"
                  >
                    <Download size={16} />
                    İndir
                  </button>
                  <button
                    onClick={closePdfPreview}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    data-testid="pdf-close-btn"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {/* PDF Iframe */}
              <div className="relative" style={{ height: '75vh' }}>
                <iframe
                  src={pdfPreview.url}
                  className="w-full h-full border-0"
                  title="PDF Preview"
                  data-testid="pdf-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanStudioDashboard;
